import {
    Constants,
    format,
    IAttribute,
    IModel,
    IViolation,
    Metamodel,
    Owned,
    stringIsNullOrEmpty
} from 'shell-models-lib';
import {
    ISapSelection,
    SapOdpCopySource,
    SapSelectionOption,
    SapSelectionSign
} from 'app/model/pipeline/copysource/sap-odp.copysource';

import { ValueAdapter } from 'app/model/adapters/value.adapter';

export enum Operator {
    EQUAL,
    NOT_EQUAL,
    LIKE,
    NOT_LIKE,
    BETWEEN,
    NOT_BETWEEN
}

@Metamodel.model({})
export class SapOdpSelection extends Owned implements ISapSelection {
    public static readonly MODEL: IModel;
    public static readonly LOW: IAttribute;
    public static readonly HIGH: IAttribute;

    @Metamodel.attribute({
        display: stringResources.FieldName,
        constraint: {
            validator: SapOdpSelection.validateFieldName
        }
    })
    public fieldName: string;

    @Metamodel.attribute({
        display: stringResources.Sign,
        constraint: {
            validator: SapOdpSelection.validateSign
        }
    })
    public sign: 'I' | 'E';

    @Metamodel.attribute({
        display: stringResources.UnpivotTransformationValuesOption,
        constraint: {
            validator: SapOdpSelection.validateOption
        }
    })
    public option: 'EQ' | 'CP' | 'BT';

    @Metamodel.attribute({
        display: stringResources.Low,
        templatable: true,
        association: 'LOW',
        constraint: {
            validator: SapOdpSelection.validateLow
        }
    })
    public low: string;

    @Metamodel.attribute({
        display: stringResources.High,
        templatable: true,
        association: 'HIGH'
    })
    public high: string;

    public get operator(): Operator {
        if (this.sign === SapSelectionSign.Inclusive) {
            if (this.option === SapSelectionOption.EQ) {
                return Operator.EQUAL;
            } else if (this.option === SapSelectionOption.CP) {
                return Operator.LIKE;
            } else if (this.option === SapSelectionOption.BT) {
                return Operator.BETWEEN;
            }
        } else if (this.sign === SapSelectionSign.Exclusive) {
            if (this.option === SapSelectionOption.EQ) {
                return Operator.NOT_EQUAL;
            } else if (this.option === SapSelectionOption.CP) {
                return Operator.NOT_LIKE;
            } else if (this.option === SapSelectionOption.BT) {
                return Operator.NOT_BETWEEN;
            }
        }
        return undefined;
    }

    public set operator(operator: Operator) {
        switch (operator) {
            case Operator.EQUAL:
                this.sign = SapSelectionSign.Inclusive;
                this.option = SapSelectionOption.EQ;
                break;
            case Operator.LIKE:
                this.option = SapSelectionOption.CP;
                this.sign = SapSelectionSign.Inclusive;
                break;
            case Operator.BETWEEN:
                this.option = SapSelectionOption.BT;
                this.sign = SapSelectionSign.Inclusive;
                break;
            case Operator.NOT_EQUAL:
                this.option = SapSelectionOption.EQ;
                this.sign = SapSelectionSign.Exclusive;
                break;
            case Operator.NOT_LIKE:
                this.option = SapSelectionOption.CP;
                this.sign = SapSelectionSign.Exclusive;
                break;
            case Operator.NOT_BETWEEN:
                this.option = SapSelectionOption.BT;
                this.sign = SapSelectionSign.Exclusive;
                break;
            default:
        }
    }

    public get showHigh(): boolean {
        return this.option === SapSelectionOption.BT;
    }

    public static validateFieldName(selection?: SapOdpSelection): IViolation[] {
        if (stringIsNullOrEmpty(selection?.fieldName)) {
            return [{
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.FieldName, stringResources.SapOdp_DisplayText, stringResources.Selection),
                redirectTo: {
                    entity: selection?.owner as SapOdpCopySource,
                    attribute: SapOdpCopySource.SELECTION
                }
            }];
        }
        return [];
    }

    public static validateSign(selection?: SapOdpSelection): IViolation[] {
        if (stringIsNullOrEmpty(selection?.sign)) {
            return [{
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.Sign, stringResources.SapOdp_DisplayText, stringResources.Selection),
                redirectTo: {
                    entity: selection?.owner as SapOdpCopySource,
                    attribute: SapOdpCopySource.SELECTION
                }
            }];
        }
        return [];
    }

    public static validateOption(selection?: SapOdpSelection): IViolation[] {
        const option = selection.option;
        if (stringIsNullOrEmpty(option)) {
            return [{
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.UnpivotTransformationValuesOption, stringResources.SapOdp_DisplayText, stringResources.Selection),
                redirectTo: {
                    entity: selection?.owner as SapOdpCopySource,
                    attribute: SapOdpCopySource.SELECTION
                }
            }];
        }

        const violations: IViolation[] = [];
        const low = ValueAdapter.toResource(selection, SapOdpSelection.LOW);
        const high = ValueAdapter.toResource(selection, SapOdpSelection.HIGH);

        // Operator CP is only allowed if wildcards ('*' or '+') are used in low.
        if (
            typeof low !== 'object'
            && option === SapSelectionOption.CP
            && Constants.wildcardsValidation.test(low)
        ) {
            violations.push({
                message: () => stringResources.SapCdc_Copy_Selection_Option_Pattern_Error,
                redirectTo: {
                    entity: selection?.owner as SapOdpCopySource,
                    attribute: SapOdpCopySource.SELECTION
                }
            });
        }

        // Operator BT is only allowed if high is filled.
        if (
            typeof high !== 'object'
            && (
                (option === SapSelectionOption.BT && stringIsNullOrEmpty(high))
                || (option !== SapSelectionOption.BT && !stringIsNullOrEmpty(high))
            )
        ) {
            violations.push({
                message: () => stringResources.SapCdc_Copy_Selection_Option_Between_Error,
                redirectTo: {
                    entity: selection?.owner as SapOdpCopySource,
                    attribute: SapOdpCopySource.SELECTION
                }
            });
        }
        return violations;
    }

    public static validateLow(selection?: SapOdpSelection): IViolation[] {
        const low = ValueAdapter.toResource(selection, SapOdpSelection.LOW);
        if (stringIsNullOrEmpty(low)) {
            return [{
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.Low, stringResources.SapOdp_DisplayText, stringResources.Selection),
                redirectTo: {
                    entity: selection?.owner as SapOdpCopySource,
                    attribute: SapOdpCopySource.SELECTION
                }
            }];
        }
        return [];
    }
}
